/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Col, Form, Input, Popover, Radio, Row, Space, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { NodeSuggestionsPanel } from '../../NodeWrapper';
import LabelWithOriginalText from '../../../Forms/Fields/LabelWithOriginalText';

// Misc imports
import { URL_REGEX } from '../../../../constants/regex';
import { NODE_TYPES } from '../../../../constants/flowGraph';
import { GraphContext } from '../../FlowGraph';
import { useBotLanguage } from '../../../../utils/hooks/useBotLanguage';

// Constant declarations
const CONNECTION_TYPES = {
  URL: 'URL',
  NODE: 'NODE'
};

/**
 * Form for an individual button in a carousel node
 * @param {Object} props The component props
 * @param {Object} props.form The form instance
 * @param {Object} props.selectedElement The selected element
 * @param {Number} props.cardIndex The index of the card
 * @param {Number} props.buttonIndex The index of the button
 * @param {String} props.sourceHandle The source handle
**/
const CarouselNodeButtonForm = ({ form, selectedElement, cardIndex, buttonIndex, sourceHandle }) => {
  const { t } = useTranslation();
  const { isEditingPrimaryLanguage, currentBotLanguage, primaryBotLanguage } = useBotLanguage();
  const { unselectNodesAndEdges } = useContext(GraphContext);

  const currentLangFormPath = ['data', 'cards', cardIndex, currentBotLanguage, 'buttons', buttonIndex];
  const primaryLangFormPath = ['data', 'cards', cardIndex, primaryBotLanguage, 'buttons', buttonIndex];

  const buttonUrl = form.getFieldValue([...currentLangFormPath, 'data']);
  const primaryButtonUrl = form.getFieldValue([...primaryLangFormPath, 'data']);
  const [buttonType, setButtonType] = useState(primaryButtonUrl ? CONNECTION_TYPES.URL : CONNECTION_TYPES.NODE);

  useEffect(() => {
    // When buttonUrl changes, set the field value
    form.setFieldValue([...currentLangFormPath, 'data'], buttonUrl);

    if (buttonUrl) {
      setButtonType(buttonUrl ? CONNECTION_TYPES.URL : CONNECTION_TYPES.NODE);
    }
  }, [buttonUrl]);

  return (
    <Fragment>
      <Row className="w-100" direction="vertical">
        <Col span={24}>
          <LabelWithOriginalText label={t('Button Label:')} originalText={form.getFieldValue([...primaryLangFormPath, 'title'])} />

          <Form.Item
            name={[...currentLangFormPath, 'title']}
          >
            <Input autoComplete="off" placeholder={t('Button')} />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Typography.Text>{t('Button Action:')}</Typography.Text>
        </Col>

        <Col span={24}>
          <Radio.Group
            className="w-100"
            value={buttonType}
            onChange={event => setButtonType(event.target.value)}
          >
            <Space className="w-100 justify-content-between mt-2" style={{ minWidth: 250 }} direction="vertical">
              <Radio value={CONNECTION_TYPES.URL}>
                {t('Open URL')}

                {
                  !isEditingPrimaryLanguage ? (
                    <Typography.Paragraph type="secondary" className="fs-xs">
                      {t('If empty, the primary language URL will be used.')}
                    </Typography.Paragraph>
                  ) : null
                }

                <Form.Item
                  className="my-2"
                  hidden={buttonType !== CONNECTION_TYPES.URL}
                  name={[...currentLangFormPath, 'data']}
                  rules={[
                    {
                      validator: async (rule, value) => {
                        try {
                          if (value && !URL_REGEX.test(value) && !/mailto:([^?]*)/.test(value)) {
                            throw new Error(rule.message);
                          }
                        } catch {
                          throw new Error(rule.message);
                        }
                      },
                      message: t('Please enter a valid URL')
                    }]}
                >
                  <Input
                    autoComplete="off"
                    placeholder={primaryButtonUrl || t('Link')}
                  />
                </Form.Item>
              </Radio>

              <Radio className="w-100" value={CONNECTION_TYPES.NODE}>
                {
                  isEditingPrimaryLanguage ? (
                    <Popover
                      className="w-100"
                      placement="right"
                      destroyTooltipOnHide={{ keepParent: false }}
                      content={
                        <NodeSuggestionsPanel
                          id={selectedElement.id}
                          type={NODE_TYPES.CAROUSEL}
                          isAddNodeTooltipOpen={true}
                          isBeforeTabVisible={false}
                          className="bg-white p-0"
                          sourceHandleToConnect={sourceHandle}
                          onSelect={async () => {
                            setButtonType(CONNECTION_TYPES.NODE);
                            await form.submit();
                            unselectNodesAndEdges();
                          }}
                        />
                      }
                    >
                      <Space align="baseline" className="justify-content-between" style={{ minWidth: 230 }}>
                        {t('Connect to node')}
                        <RightOutlined />
                      </Space>
                    </Popover>
                  ) : (
                    <Space align="baseline" className="justify-content-between" style={{ minWidth: 230 }}>
                      {t('Connect to node')}
                      <RightOutlined />
                    </Space>
                  )}
              </Radio>
            </Space>
          </Radio.Group>
        </Col>

        {/* Hidden fields */}
        <Form.Item
          name={[...primaryLangFormPath, 'type']}
          hidden
        >
          <Input />
        </Form.Item>

        <Form.Item
          name={[...primaryLangFormPath, 'tooltip']}
          hidden
        >
          <Input />
        </Form.Item>
      </Row>
    </Fragment>
  );
};

CarouselNodeButtonForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedElement: PropTypes.object,
  cardIndex: PropTypes.number,
  buttonIndex: PropTypes.number,
  sourceHandle: PropTypes.string
};

export default CarouselNodeButtonForm;
